import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { RichTextEditor } from '@/components/ui/rich-text-editor';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import { GripVertical, Upload, Loader2, Trash2, MoreVertical, IndentIncrease, IndentDecrease, Plus, X, ClipboardPaste } from 'lucide-react';
import { ImageItem } from '@/lib/image-utils';
import { MultiImageUpload } from './MultiImageUpload';
import { Step, ImageTarget } from './StepEditor';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import { CompactRoleSelector } from './CompactRoleSelector';
import { RoleBadgeList } from './RoleBadge';

interface StepItemProps {
  step: Step;
  steps: Step[];
  uploading: Set<string>;
  onStepChange: (tempId: string, field: keyof Step, value: any) => void;
  onRemove: (tempId: string) => void;
  onAddSubstep: (parentTempId: string) => void;
  onMakeIndependent: (tempId: string) => void;
  onMakeSubstep: (tempId: string, parentTempId: string) => void;
  onImageUpload: (tempId: string, file: File, isToolsImage?: boolean) => Promise<void>;
  isSubstep?: boolean;
  registerStepRef?: (tempId: string, el: HTMLDivElement | null) => void;
  onImageTargetFocus?: (target: ImageTarget) => void;
  onPasteImage?: (target: ImageTarget) => void;
  enableDragReorder?: boolean;
}

export const StepItem = ({
  step,
  steps,
  uploading,
  onStepChange,
  onRemove,
  onAddSubstep,
  onMakeIndependent,
  onMakeSubstep,
  onImageUpload,
  isSubstep = false,
  registerStepRef,
  onImageTargetFocus,
  onPasteImage,
  enableDragReorder = true,
}: StepItemProps) => {
  const { t } = useTranslation();
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: step.tempId });

  const [isDraggingOver, setIsDraggingOver] = useState(false);
  const [isToolsDraggingOver, setIsToolsDraggingOver] = useState(false);

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const mainSteps = steps.filter(s => !s.parent_step_id && s.tempId !== step.tempId);

  const setActiveTarget = (type: ImageTarget['type']) => {
    onImageTargetFocus?.({ tempId: step.tempId, type });
  };

  const requestPaste = (type: ImageTarget['type']) => {
    setActiveTarget(type);
    onPasteImage?.({ tempId: step.tempId, type });
  };

  const handleDragOver = (e: React.DragEvent, isTools = false) => {
    e.preventDefault();
    e.stopPropagation();
    setActiveTarget(isTools ? 'tools' : 'main');
    if (isTools) {
      setIsToolsDraggingOver(true);
    } else {
      setIsDraggingOver(true);
    }
  };

  const handleDragLeave = (e: React.DragEvent, isTools = false) => {
    e.preventDefault();
    e.stopPropagation();
    if (isTools) {
      setIsToolsDraggingOver(false);
    } else {
      setIsDraggingOver(false);
    }
  };

  const handleDrop = async (e: React.DragEvent, isTools = false) => {
    e.preventDefault();
    e.stopPropagation();
    setActiveTarget(isTools ? 'tools' : 'main');

    if (isTools) {
      setIsToolsDraggingOver(false);
    } else {
      setIsDraggingOver(false);
    }

    const files = e.dataTransfer.files;
    if (files && files[0]) {
      const file = files[0];
      if (file.type.startsWith('image/')) {
        await onImageUpload(step.tempId, file, isTools);
      } else {
        toast.error(t('errors.notImage') || 'Only image uploads are supported here.');
      }
    }
  };

  return (
    <>
      <div
        ref={(el) => {
          setNodeRef(el);
          if (registerStepRef) registerStepRef(step.tempId, el);
        }}
        style={style}
        className={isSubstep ? 'ml-12' : ''}
      >
        <Card className="relative">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                {enableDragReorder && (
                  <button
                    className="cursor-grab active:cursor-grabbing touch-none"
                    {...attributes}
                    {...listeners}
                  >
                    <GripVertical className="h-5 w-5 text-muted-foreground" />
                  </button>
                )}
                <CardTitle className="text-lg">
                  {isSubstep ? (
                    <span className="text-muted-foreground">
                      {step.step_number} · {t('editor.stepTitle')}
                    </span>
                  ) : (
                    <>
                      {t('editor.step')} {step.step_number}
                    </>
                  )}
                </CardTitle>
              </div>
              
              <div className="flex items-center gap-2">
                {!isSubstep && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => onAddSubstep(step.tempId)}
                  >
                    <Plus className="h-4 w-4 mr-1" />
                    {t('editor.addSubstep')}
                  </Button>
                )}
                
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="sm">
                      <MoreVertical className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="bg-background">
                    {isSubstep && (
                      <>
                        <DropdownMenuItem onClick={() => onMakeIndependent(step.tempId)}>
                          <IndentDecrease className="mr-2 h-4 w-4" />
                          {t('editor.makeIndependent')}
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                      </>
                    )}
                    {!isSubstep && mainSteps.length > 0 && (
                      <>
                        <DropdownMenuItem disabled className="text-xs text-muted-foreground">
                          {t('editor.groupUnder')}
                        </DropdownMenuItem>
                        {mainSteps.map(mainStep => (
                          <DropdownMenuItem
                            key={mainStep.tempId}
                            onClick={() => onMakeSubstep(step.tempId, mainStep.tempId)}
                            className="pl-6"
                          >
                            <IndentIncrease className="mr-2 h-4 w-4" />
                            {t('editor.step')} {mainStep.step_number}
                          </DropdownMenuItem>
                        ))}
                        <DropdownMenuSeparator />
                      </>
                    )}
                    <DropdownMenuItem
                      onClick={() => onRemove(step.tempId)}
                      className="text-destructive"
                    >
                      <Trash2 className="mr-2 h-4 w-4" />
                      {t('instructions.delete')}
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </div>
          </CardHeader>
          
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label>{t('editor.stepTitle')} *</Label>
              <Input
                type="text"
                placeholder={t('editor.stepTitlePlaceholder')}
                value={step.title}
                onChange={(e) => onStepChange(step.tempId, 'title', e.target.value)}
              />
            </div>

            {/* Compact role selector - only for top-level steps */}
            {!isSubstep && (
              <CompactRoleSelector
                selectedRoles={step.required_roles}
                onChange={(roles) => onStepChange(step.tempId, 'required_roles', roles)}
                label={t('editor.requiredRoles')}
                description={t('editor.requiredRolesDescription')}
              />
            )}

            {isSubstep && (
              <>
                <div className="space-y-2">
                  <Label>{t('editor.instructions')} *</Label>
                  <RichTextEditor
                    placeholder={t('editor.instructionsPlaceholder')}
                    value={step.description || ''}
                    onChange={(html) => onStepChange(step.tempId, 'description', html)}
                  />
                </div>

                <MultiImageUpload
                  images={step.image_url || []}
                  label={t('editor.referenceImage')}
                  tempId={`main-${step.tempId}`}
                  uploading={uploading.has(step.tempId)}
                  onImageUpload={(file) => onImageUpload(step.tempId, file, false)}
                  onImagesChange={(images) => onStepChange(step.tempId, 'image_url', images)}
                  onPaste={() => requestPaste('main')}
                  onFocus={() => setActiveTarget('main')}
                />

                <div className="space-y-2">
                  <Label>{t('editor.partNumber')}</Label>
                  <Input
                    type="text"
                    placeholder={t('editor.partNumberPlaceholder')}
                    value={step.part_number || ''}
                    onChange={(e) => onStepChange(step.tempId, 'part_number', e.target.value)}
                  />
                </div>

                <div className="space-y-2">
                  <Label>{t('editor.toolsEquipment')}</Label>
                  <Input
                    type="text"
                    placeholder={t('editor.toolsEquipmentPlaceholder')}
                    value={step.tools_equipment || ''}
                    onChange={(e) => onStepChange(step.tempId, 'tools_equipment', e.target.value)}
                  />
                  <MultiImageUpload
                    images={step.tools_equipment_image_url || []}
                    label={t('editor.toolsEquipmentImage')}
                    tempId={`tools-${step.tempId}`}
                    uploading={uploading.has(`${step.tempId}-tools`)}
                    onImageUpload={(file) => onImageUpload(step.tempId, file, true)}
                    onImagesChange={(images) => onStepChange(step.tempId, 'tools_equipment_image_url', images)}
                    onPaste={() => requestPaste('tools')}
                    onFocus={() => setActiveTarget('tools')}
                  />
                </div>

                <div className="space-y-2">
                  <Label>{t('editor.ppe')}</Label>
                  <Input
                    type="text"
                    placeholder={t('editor.ppePlaceholder')}
                    value={step.ppe || ''}
                    onChange={(e) => onStepChange(step.tempId, 'ppe', e.target.value)}
                  />
                </div>

                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id={`quality-${step.tempId}`}
                    checked={step.is_quality_check}
                    onChange={(e) => onStepChange(step.tempId, 'is_quality_check', e.target.checked)}
                    className="h-4 w-4"
                  />
                  <Label htmlFor={`quality-${step.tempId}`} className="cursor-pointer">
                    {t('editor.qualityInspection')}
                  </Label>
                </div>

                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id={`evidence-${step.tempId}`}
                    checked={step.evidence_required}
                    onChange={(e) => onStepChange(step.tempId, 'evidence_required', e.target.checked)}
                    className="h-4 w-4"
                  />
                  <Label htmlFor={`evidence-${step.tempId}`} className="cursor-pointer">
                    {t('editor.evidenceRequired')}
                  </Label>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Render substeps */}
      {step.substeps && step.substeps.length > 0 && (
        <div className="space-y-4">
          {step.substeps.map((substep) => (
            <StepItem
              key={substep.tempId}
              step={substep}
              steps={steps}
              uploading={uploading}
              onStepChange={onStepChange}
              onRemove={onRemove}
              onAddSubstep={onAddSubstep}
              onMakeIndependent={onMakeIndependent}
              onMakeSubstep={onMakeSubstep}
              onImageUpload={onImageUpload}
              isSubstep={true}
              registerStepRef={registerStepRef}
              onImageTargetFocus={onImageTargetFocus}
              onPasteImage={onPasteImage}
              enableDragReorder={enableDragReorder}
            />
          ))}
        </div>
      )}
    </>
  );
};
